import { useState, useEffect, useCallback } from 'react';
import { FavoritesService, FavoriteContent } from '@/services/favoritesService';
import { useAuth } from '@/hooks/useAuth';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';

export const useFavorites = () => {
  const [favorites, setFavorites] = useState<FavoriteContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { authState } = useAuth();
  const { campaignCountriesLanguagesId } = useCampaignConfig();

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!authState.user?.smartuserId || !campaignCountriesLanguagesId) {
        setFavorites([]);
        setLoading(false);
        return;
      }

      console.log(`🔍 [${new Date().toISOString()}] useFavorites: Starting fetchFavorites with:`, {
        smartuserId: authState.user.smartuserId,
        campaignCountriesLanguagesId
      });

      try {
        setLoading(true);
        setError(null);

        const data = await FavoritesService.getUserFavorites(
          campaignCountriesLanguagesId,
          authState.user.smartuserId
        );
        setFavorites(data);
      } catch (err) {
        console.error('🔍 useFavorites: Error in fetchFavorites:', err);
        setError('Error loading favorites');
        console.error(err);
        setFavorites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, [authState.user?.smartuserId, campaignCountriesLanguagesId]);

  const addToFavorites = async (contentId: number, contentType: 'serie' | 'episode') => {
    if (!authState.user?.smartuserId) return false;

    const success = await FavoritesService.addToFavorites(
      authState.user.smartuserId,
      contentId,
      contentType
    );

    if (success) {
      // OPTIMIZATION: Cache is already updated by addToFavorites, just refetch from cache
      console.log('⭐ [FAVORITES] Favorite added, refreshing from cache...');
      const updatedFavorites = await FavoritesService.getUserFavorites(
        campaignCountriesLanguagesId!,
        authState.user.smartuserId
      );
      setFavorites(updatedFavorites);
    }

    return success;
  };

  const removeFromFavorites = async (contentId: number, contentType: 'serie' | 'episode') => {
    if (!authState.user?.smartuserId) return false;

    const success = await FavoritesService.removeFromFavorites(
      authState.user.smartuserId,
      contentId,
      contentType
    );

    if (success) {
      // OPTIMIZATION: Cache is already updated by removeFromFavorites, just refetch from cache
      console.log('⭐ [FAVORITES] Favorite removed, refreshing from cache...');
      const updatedFavorites = await FavoritesService.getUserFavorites(
        campaignCountriesLanguagesId!,
        authState.user.smartuserId
      );
      setFavorites(updatedFavorites);
    }

    return success;
  };

  const toggleFavorite = async (contentId: number, contentType: 'serie' | 'episode'): Promise<{ success: boolean; isFavoriteAfter: boolean }> => {
    console.log('❤️ useFavorites toggleFavorite called:', {
      contentId,
      contentType,
      smartuserId: authState.user?.smartuserId || 'NO USER',
      campaignCountriesLanguagesId
    });

    if (!authState.user?.smartuserId) {
      console.log('❤️ useFavorites toggleFavorite: No smartuserId, returning false');
      return { success: false, isFavoriteAfter: false };
    }

    console.log('❤️ useFavorites toggleFavorite: Calling FavoritesService.toggleFavorite');
    const result = await FavoritesService.toggleFavorite(
      authState.user.smartuserId,
      contentId,
      contentType
    );

    console.log('❤️ useFavorites toggleFavorite: Service returned:', result);

    if (result.success) {
      const updatedFavorites = await FavoritesService.getUserFavorites(
        campaignCountriesLanguagesId!,
        authState.user.smartuserId
      );
      setFavorites(updatedFavorites);
      console.log('⭐ [FAVORITES] Favorites list refreshed from cache');
    }

    return result;
  };

  const isFavorite = async (contentId: number, contentType: 'serie' | 'episode') => {
    if (!authState.user?.smartuserId) return false;

    return await FavoritesService.isFavorite(
      authState.user.smartuserId,
      contentId,
      contentType
    );
  };

  const refetchFavorites = useCallback(async () => {
    if (!authState.user?.smartuserId || !campaignCountriesLanguagesId) {
      setFavorites([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await FavoritesService.getUserFavorites(
        campaignCountriesLanguagesId,
        authState.user.smartuserId
      );
      setFavorites(data);
    } catch (err) {
      console.error('🔍 useFavorites: Error in refetchFavorites:', err);
      setError('Error loading favorites');
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  }, [authState.user?.smartuserId, campaignCountriesLanguagesId]);

  return {
    favorites,
    loading,
    error,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    refetchFavorites,
  };
};